import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  ButtonGroup,
  Pagination,
  CircularProgress,
  Stack,
} from '@mui/material';
import { Lightbulb, ThumbUp, ThumbDown } from '@mui/icons-material';
import { where } from 'firebase/firestore';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { MainLayout } from '../components/layout/MainLayout';
import { usePagination } from '../hooks/usePagination';
import { Suggestion, getStatusDisplayName, getStatusColor } from '../types/suggestion';

type FilterType = 'all' | 'in_progress' | 'closed' | 'completed' | 'rejected';

interface LocationState {
  page?: number;
  filter?: FilterType;
}

export const SuggestionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Restore state from navigation
  const locationState = location.state as LocationState | null;
  const [filter, setFilter] = useState<FilterType>(locationState?.filter || 'all');

  // Build Firestore query constraints
  const getConstraints = () => {
    const constraints: any[] = [];

    // Status filter
    if (filter !== 'all') {
      constraints.push(where('status', '==', filter));
    }

    // Note: orderBy('createdAt', 'desc') is already added by usePagination hook

    return constraints;
  };

  // Pagination hook
  const {
    items: suggestions,
    loading,
    currentPage,
    hasNextPage,
    hasPrevPage,
    loadFirstPage,
    loadNextPage,
    loadPrevPage,
    goToPage,
  } = usePagination<Suggestion>('suggestions', { pageSize: 10 }, ...getConstraints());

  // Reload on filter change
  useEffect(() => {
    if (locationState?.page && locationState?.filter === filter) {
      goToPage(locationState.page);
      window.history.replaceState({}, '');
    } else {
      loadFirstPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Navigate to detail
  const handleSuggestionClick = (suggestionId: string) => {
    navigate(`/suggestions/${suggestionId}`, {
      state: {
        from: '/suggestions',
        page: currentPage,
        filter: filter,
      },
    });
  };

  return (
    <MainLayout>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Lightbulb sx={{ fontSize: 32, color: '#667eea' }} />
          <Box>
            <Typography variant="h4" component="h1" fontWeight={700}>
              제안 관리
            </Typography>
            <Typography variant="body2" color="text.secondary">
              사용자 제안사항을 확인하고 답변합니다.
            </Typography>
          </Box>
        </Box>

        {/* Status Filter Buttons */}
        <Box sx={{ mb: 3 }}>
          <ButtonGroup variant="outlined">
            <Button
              variant={filter === 'all' ? 'contained' : 'outlined'}
              onClick={() => setFilter('all')}
            >
              전체
            </Button>
            <Button
              variant={filter === 'in_progress' ? 'contained' : 'outlined'}
              onClick={() => setFilter('in_progress')}
            >
              진행중
            </Button>
            <Button
              variant={filter === 'completed' ? 'contained' : 'outlined'}
              onClick={() => setFilter('completed')}
            >
              완료
            </Button>
            <Button
              variant={filter === 'rejected' ? 'contained' : 'outlined'}
              onClick={() => setFilter('rejected')}
            >
              거부
            </Button>
            <Button
              variant={filter === 'closed' ? 'contained' : 'outlined'}
              onClick={() => setFilter('closed')}
            >
              종료
            </Button>
          </ButtonGroup>
        </Box>

        {/* Suggestions Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, width: '40%' }}>제목</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '10%' }}>카테고리</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '12%' }}>소속</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '10%', textAlign: 'center' }}>투표</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '13%' }}>작성일</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '10%', textAlign: 'center' }}>상태</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '5%', textAlign: 'center' }}>답변</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : suggestions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">
                        제안사항이 없습니다.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  suggestions.map((suggestion) => (
                    <TableRow
                      key={suggestion.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleSuggestionClick(suggestion.id)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {suggestion.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {suggestion.content}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={suggestion.category}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{suggestion.userOrganization || '미지정'}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ThumbUp sx={{ fontSize: 16, color: '#10b981' }} />
                            <Typography variant="caption">{suggestion.upvotes}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ThumbDown sx={{ fontSize: 16, color: '#ef4444' }} />
                            <Typography variant="caption">{suggestion.downvotes}</Typography>
                          </Box>
                          {(() => {
                            const netVotes = suggestion.upvotes - suggestion.downvotes;
                            const color = netVotes > 0 ? '#10b981' : netVotes < 0 ? '#ef4444' : '#6b7280';
                            return (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  color: color,
                                  ml: 0.5,
                                }}
                              >
                                ({netVotes > 0 ? '+' : ''}{netVotes})
                              </Typography>
                            );
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {suggestion.createdAt
                          ? format(suggestion.createdAt.toDate(), 'yyyy-MM-dd HH:mm', {
                              locale: ko,
                            })
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getStatusDisplayName(suggestion.status)}
                          color={getStatusColor(suggestion.status)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {suggestion.reply ? (
                          <Chip
                            label="완료"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            label="미답변"
                            color="default"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {!loading && suggestions.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Stack spacing={2} alignItems="center">
                <Pagination
                  count={Math.max(5, hasNextPage ? currentPage + 1 : currentPage)}
                  page={currentPage}
                  onChange={(_, page) => goToPage(page)}
                  color="primary"
                  showFirstButton
                  showLastButton={false}
                  siblingCount={1}
                  boundaryCount={1}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    onClick={loadPrevPage}
                    disabled={!hasPrevPage || loading}
                  >
                    이전
                  </Button>
                  <Button
                    size="small"
                    onClick={loadNextPage}
                    disabled={!hasNextPage || loading}
                  >
                    다음
                  </Button>
                </Box>
              </Stack>
            </Box>
          )}
        </Paper>
      </Box>
    </MainLayout>
  );
};
